<template>
  <div class="panel-tv">
    <header class="panel-header">
      <div class="panel-brand">
        <span class="panel-logo">🎧</span>
        <div>
          <h1 class="panel-title">Mesa de Ayuda — Panel en vivo</h1>
          <p class="panel-subtitle">Monitoreo de tickets y métricas operativas</p>
        </div>
      </div>
      <div class="panel-header-meta">
        <div class="live-pill">
          <span class="live-dot"></span>
          En vivo
        </div>
        <div class="panel-clock">{{ horaActual }}</div>
        <div class="panel-date">{{ fechaActual }}</div>
      </div>
    </header>

    <div class="kpi-strip">
      <div v-for="card in kpiCards" :key="card.title" class="kpi-tile" :class="'kpi-tile--' + card.color">
        <div class="kpi-tile-value">{{ card.value }}</div>
        <div class="kpi-tile-label">{{ card.title }}</div>
      </div>
    </div>

    <main class="panel-main">
      <div class="panel-slide-wrap">
      <transition name="panel-fade" mode="out-in">
        <!-- PANTALLA 1: Tabla en vivo -->
        <section v-if="slideActual === 0" key="tickets" class="panel-screen">
          <div class="screen-head">
            <h2>Tickets recientes</h2>
            <span class="screen-badge">{{ totalItems }} registrados · últimos tickets</span>
          </div>
          <div class="table-shell">
            <table class="tv-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Título</th>
                  <th>Área</th>
                  <th>Subárea</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Asignado</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in dataTicket" :key="item.id" class="tv-row">
                  <td class="col-ticket">{{ item.numero_ticket_formateado || item.id }}</td>
                  <td class="col-titulo">{{ item.titulo }}</td>
                  <td>{{ item.area || '—' }}</td>
                  <td>{{ item.subarea || '—' }}</td>
                  <td>{{ item.cliente_completo || '—' }}</td>
                  <td class="col-fecha">{{ item.fecha_creacion }}</td>
                  <td>{{ item.usuario || 'Sin asignar' }}</td>
                  <td>
                    <span :class="['estado-chip', estadoClase(item.estado_actual)]">
                      {{ item.estado_actual }}
                    </span>
                  </td>
                </tr>
                <tr v-if="!dataTicket.length">
                  <td colspan="8" class="empty-row">Sin tickets para mostrar</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- PANTALLA 2: Resumen operativo (1/2) -->
        <section v-else-if="slideActual === 1" key="resumen1" class="panel-screen panel-screen--charts">
          <div class="screen-head screen-head--compact">
            <h2>Resumen operativo</h2>
            <span class="screen-badge">Estados y tendencia · 1/2</span>
          </div>
          <div class="charts-grid charts-grid--tv-2col">
            <div class="chart-panel chart-panel--large">
              <h3>Tickets por estado</h3>
              <div class="chart-box" :style="chartBoxStyleDuo">
                <doughnut-chart :chart-data="dataPorEstado" :options="chartOptionsPie" />
              </div>
            </div>
            <div class="chart-panel chart-panel--large">
              <h3>Aperturas vs cierres</h3>
              <div class="chart-box" :style="chartBoxStyleDuo">
                <line-chart :chart-data="dataAperturasCierres" :options="chartOptionsLine" />
              </div>
            </div>
          </div>
        </section>

        <!-- PANTALLA 3: Resumen operativo (2/2) -->
        <section v-else-if="slideActual === 2" key="resumen2" class="panel-screen panel-screen--charts">
          <div class="screen-head screen-head--compact">
            <h2>Resumen operativo</h2>
            <span class="screen-badge">Solicitudes y áreas · 2/2</span>
          </div>
          <div class="charts-grid charts-grid--tv-2col">
            <div class="chart-panel chart-panel--large">
              <h3>Top solicitudes</h3>
              <div class="chart-box" :style="chartBoxStyleDuo">
                <bar-chart :chart-data="dataTopSolicitudes" :options="chartOptionsBar" />
              </div>
            </div>
            <div class="chart-panel chart-panel--large">
              <h3>Tickets por área</h3>
              <div class="chart-box" :style="chartBoxStyleDuo">
                <horizontal-bar-chart :chart-data="dataPorArea" :options="chartOptionsHBar" />
              </div>
            </div>
          </div>
        </section>

        <!-- PANTALLA 4: Métricas avanzadas -->
        <section v-else-if="slideActual === 3" key="charts2" class="panel-screen panel-screen--charts">
          <div class="screen-head screen-head--compact">
            <h2>Análisis por área</h2>
            <span class="screen-badge">Rendimiento y carga de trabajo</span>
          </div>
          <div class="charts-grid charts-grid--tv charts-grid--tv-2col">
            <div class="chart-panel">
              <h3>Estados por área</h3>
              <div class="chart-box" :style="chartBoxStyleWide">
                <horizontal-bar-chart :chart-data="dataEstadoPorArea" :options="chartOptionsHStacked" />
              </div>
            </div>
            <div class="chart-panel">
              <h3>Tiempo prom. de cierre (horas)</h3>
              <div class="chart-box" :style="chartBoxStyleWide">
                <bar-chart :chart-data="dataTiempoCierreArea" :options="chartOptionsBar" />
              </div>
            </div>
          </div>
          <div class="stats-row stats-row--compact">
            <div v-for="stat in statsDestacados" :key="stat.label" class="stat-card">
              <div class="stat-icon">{{ stat.icon }}</div>
              <div>
                <div class="stat-value">{{ stat.value }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
          </div>
        </section>
      </transition>
      </div>
    </main>

    <footer class="panel-footer">
      <div class="slide-tabs">
        <button
          v-for="(nombre, idx) in nombresSlides"
          :key="nombre"
          type="button"
          :class="['slide-tab', { active: slideActual === idx }]"
          @click="irASlide(idx)"
        >
          {{ nombre }}
        </button>
        <button
          type="button"
          :class="['slide-tab', 'slide-tab--pause', { active: rotacionPausada }]"
          @click="alternarPausaRotacion"
        >
          {{ rotacionPausada ? '▶ Reanudar' : '⏸ Pausar' }}
        </button>
      </div>
      <div class="slide-progress" :class="{ 'slide-progress--paused': rotacionPausada }">
        <div class="slide-progress-bar" :style="{ width: progresoSlide + '%' }"></div>
      </div>
      <div class="slide-counter">
        Pantalla {{ slideActual + 1 }} / {{ totalSlides }}
        <span v-if="rotacionPausada" class="slide-paused-label">· Pausado</span>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from 'axios';
import { Notification } from 'element-ui';
import { connectSocket } from '@/services/socketService/socketService';
import { Bar, Doughnut, Line, HorizontalBar, mixins } from 'vue-chartjs';

export const RUTA_API = process.env.VUE_APP_API;
const { reactiveProp } = mixins;

const createReactiveChart = (Base) => ({
  extends: Base,
  mixins: [reactiveProp],
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      deep: true,
      handler(val) {
        if (this.$data._chart) {
          this.$data._chart.destroy();
        }
        this.renderChart(val, this.options);
      },
    },
  },
});

export default {
  name: 'PanelTicketsTV',
  components: {
    BarChart: createReactiveChart(Bar),
    DoughnutChart: createReactiveChart(Doughnut),
    HorizontalBarChart: createReactiveChart(HorizontalBar),
    LineChart: createReactiveChart(Line),
  },
  data() {
    return {
      dataTicket: [],
      dashboard: null,
      headers: [],
      lastTicketSnapshot: [],
      socket: null,
      totalItems: 0,
      slideActual: 0,
      totalSlides: 4,
      nombresSlides: ['Tickets', 'Resumen I', 'Resumen II', 'Análisis'],
      intervaloRotacion: null,
      intervaloProgreso: null,
      intervaloReloj: null,
      intervaloDashboard: null,
      progresoSlide: 0,
      horaActual: '',
      fechaActual: '',
      rotacionMs: 5000,
      tickProgresoMs: 50,
      rotacionPausada: false,
      viewportAlto: typeof window !== 'undefined' ? window.innerHeight : 900,
    };
  },
  computed: {
    chartAlturaTvDuo() {
      return Math.max(220, this.viewportAlto - 330);
    },
    chartBoxStyleDuo() {
      return { height: `${this.chartAlturaTvDuo}px` };
    },
    chartAlturaTvWide() {
      return Math.max(180, this.viewportAlto - 360);
    },
    chartBoxStyleWide() {
      return { height: `${this.chartAlturaTvWide}px` };
    },
    kpiCards() {
      const k = (this.dashboard && this.dashboard.kpis) || {};
      const abiertos = k.abiertos || 0;
      const total = k.total_rango || this.totalItems || 0;
      const prom = k.prom_horas_cierre != null ? `${Number(k.prom_horas_cierre).toFixed(1)} h` : '—';
      const enTabla = this.dataTicket.length;
      return [
        { title: 'Total en rango', value: total, color: 'blue' },
        { title: 'Abiertos', value: abiertos, color: 'amber' },
        { title: 'Prom. cierre', value: prom, color: 'green' },
        { title: 'En pantalla', value: enTabla, color: 'violet' },
      ];
    },
    statsDestacados() {
      const porEstado = ((this.dashboard && this.dashboard.distribuciones && this.dashboard.distribuciones.por_estado) || []);
      const nuevos = porEstado.find((e) => e.estado === 'Nuevo');
      const resueltos = porEstado.find((e) => e.estado === 'Resuelto');
      const areas = ((this.dashboard && this.dashboard.distribuciones && this.dashboard.distribuciones.por_area) || []);
      const areaTop = areas.length ? areas.reduce((a, b) => (a.n >= b.n ? a : b)) : null;
      return [
        { icon: '🆕', value: nuevos ? nuevos.n : 0, label: 'Tickets nuevos' },
        { icon: '✅', value: resueltos ? resueltos.n : 0, label: 'Resueltos' },
        { icon: '🏢', value: areaTop ? areaTop.area : '—', label: 'Área con más tickets' },
        { icon: '📊', value: areas.length, label: 'Áreas activas' },
      ];
    },
    dataAperturasCierres() {
      const s = (this.dashboard && this.dashboard.series) || {};
      const aperturas = s.aperturas || [];
      const cierres = s.cierres || [];
      const allDays = Array.from(new Set([
        ...aperturas.map((x) => x.d),
        ...cierres.map((x) => x.d),
      ])).sort();
      const mapA = aperturas.reduce((acc, x) => { acc[x.d] = x.n; return acc; }, {});
      const mapC = cierres.reduce((acc, x) => { acc[x.d] = x.n; return acc; }, {});
      return {
        labels: allDays,
        datasets: [
          {
            label: 'Aperturas',
            borderColor: '#60A5FA',
            backgroundColor: 'rgba(96,165,250,0.2)',
            fill: true,
            data: allDays.map((d) => mapA[d] || 0),
          },
          {
            label: 'Cierres',
            borderColor: '#34D399',
            backgroundColor: 'rgba(52,211,153,0.15)',
            fill: true,
            data: allDays.map((d) => mapC[d] || 0),
          },
        ],
      };
    },
    dataPorEstado() {
      const arr = (this.dashboard && this.dashboard.distribuciones && this.dashboard.distribuciones.por_estado) || [];
      return {
        labels: arr.map((x) => x.estado),
        datasets: [{
          backgroundColor: ['#34D399', '#60A5FA', '#FBBF24', '#F87171', '#A78BFA', '#22D3EE'],
          data: arr.map((x) => x.n),
        }],
      };
    },
    dataTopSolicitudes() {
      const arr = (this.dashboard && this.dashboard.distribuciones && this.dashboard.distribuciones.top_solicitudes) || [];
      return {
        labels: arr.map((x) => x.titulo),
        datasets: [{ label: 'Solicitudes', backgroundColor: '#818CF8', data: arr.map((x) => x.n) }],
      };
    },
    dataEstadoPorArea() {
      const arr = (this.dashboard && this.dashboard.distribuciones && this.dashboard.distribuciones.estado_por_area) || [];
      const areas = Array.from(new Set(arr.map((x) => x.area)));
      const estados = Array.from(new Set(arr.map((x) => x.estado)));
      const colorPool = ['#34D399', '#60A5FA', '#FBBF24', '#F87171', '#A78BFA', '#22D3EE', '#10B981', '#F472B6'];
      const matrix = {};
      arr.forEach((x) => {
        matrix[x.area] = matrix[x.area] || {};
        matrix[x.area][x.estado] = x.n;
      });
      const datasets = estados.map((estado, idx) => ({
        label: estado,
        backgroundColor: colorPool[idx % colorPool.length],
        data: areas.map((a) => (matrix[a] && matrix[a][estado]) ? matrix[a][estado] : 0),
      }));
      return { labels: areas, datasets };
    },
    dataTiempoCierreArea() {
      const arr = (this.dashboard && this.dashboard.distribuciones && this.dashboard.distribuciones.tiempo_cierre_area) || [];
      return {
        labels: arr.map((x) => x.area),
        datasets: [{ label: 'Promedio (h)', backgroundColor: '#22D3EE', data: arr.map((x) => Number(x.horas_prom || 0)) }],
      };
    },
    dataPorArea() {
      const arr = (this.dashboard && this.dashboard.distribuciones && this.dashboard.distribuciones.por_area) || [];
      return {
        labels: arr.map((x) => x.area),
        datasets: [{ label: 'Tickets', backgroundColor: '#34D399', data: arr.map((x) => x.n) }],
      };
    },
    chartOptionsBase() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 4, right: 6, bottom: 4, left: 4 } },
      };
    },
    chartOptionsLine() {
      return {
        ...this.chartOptionsBase,
        legend: {
          position: 'bottom',
          labels: { fontColor: '#E2E8F0', fontSize: 9, usePointStyle: true, boxWidth: 8, padding: 6 },
        },
        tooltips: { mode: 'index', intersect: false },
        elements: { line: { tension: 0.35, borderWidth: 2 }, point: { radius: 0 } },
        scales: {
          xAxes: [{ gridLines: { color: 'rgba(255,255,255,0.08)' }, ticks: { fontColor: '#CBD5E1', fontSize: 9, maxRotation: 0, autoSkip: true, maxTicksLimit: 6 } }],
          yAxes: [{ gridLines: { color: 'rgba(255,255,255,0.08)' }, ticks: { fontColor: '#CBD5E1', fontSize: 9, beginAtZero: true, precision: 0 } }],
        },
      };
    },
    chartOptionsBar() {
      return {
        ...this.chartOptionsLine,
        legend: { display: false },
        layout: { padding: { top: 6, right: 6, bottom: 2, left: 4 } },
      };
    },
    chartOptionsHBar() {
      return {
        ...this.chartOptionsLine,
        legend: { display: false },
        layout: { padding: { top: 4, right: 8, bottom: 4, left: 4 } },
        scales: {
          xAxes: [{ gridLines: { color: 'rgba(255,255,255,0.08)' }, ticks: { fontColor: '#CBD5E1', fontSize: 9, beginAtZero: true } }],
          yAxes: [{ gridLines: { display: false }, ticks: { fontColor: '#CBD5E1', fontSize: 8, padding: 2 } }],
        },
      };
    },
    chartOptionsHStacked() {
      return {
        ...this.chartOptionsLine,
        legend: {
          position: 'right',
          align: 'center',
          labels: { fontColor: '#E2E8F0', fontSize: 8, boxWidth: 8, padding: 6 },
        },
        layout: { padding: { top: 4, right: 4, bottom: 4, left: 4 } },
        scales: {
          xAxes: [{ stacked: true, gridLines: { color: 'rgba(255,255,255,0.08)' }, ticks: { fontColor: '#CBD5E1', fontSize: 9, beginAtZero: true } }],
          yAxes: [{ stacked: true, gridLines: { display: false }, ticks: { fontColor: '#CBD5E1', fontSize: 8 } }],
        },
      };
    },
    chartOptionsPie() {
      return {
        ...this.chartOptionsBase,
        legend: {
          position: 'right',
          align: 'center',
          labels: {
            fontColor: '#E2E8F0',
            fontSize: 9,
            boxWidth: 10,
            padding: 8,
          },
        },
      };
    },
  },
  watch: {
    slideActual(val) {
      if (val >= 1 && val <= 3) {
        this.actualizarViewportAlto();
        this.$nextTick(() => {
          window.dispatchEvent(new Event('resize'));
        });
      }
    },
    viewportAlto() {
      if (this.slideActual >= 1 && this.slideActual <= 3) {
        this.$nextTick(() => {
          window.dispatchEvent(new Event('resize'));
        });
      }
    },
  },
  created() {
    this.obtenerTickets();
    this.fetchDashboard();
    this.actualizarReloj();
  },
  mounted() {
    this.conectarWebSocket();
    this.iniciarRotacion();
    this.intervaloReloj = setInterval(this.actualizarReloj, 1000);
    this.intervaloDashboard = setInterval(this.fetchDashboard, 60000);
    this.actualizarViewportAlto();
    window.addEventListener('resize', this.actualizarViewportAlto);
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  },
  beforeDestroy() {
    this.detenerRotacion();
    clearInterval(this.intervaloReloj);
    clearInterval(this.intervaloDashboard);
    window.removeEventListener('resize', this.actualizarViewportAlto);
    if (this.socket) this.socket.close();
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  },
  methods: {
    actualizarViewportAlto() {
      this.viewportAlto = window.innerHeight;
    },
    estadoClase(estado) {
      const map = {
        Nuevo: 'estado-nuevo',
        Asignado: 'estado-asignado',
        'En proceso': 'estado-proceso',
        'En espera': 'estado-espera',
        Resuelto: 'estado-resuelto',
        Cancelado: 'estado-cancelado',
        Cerrado: 'estado-cerrado',
      };
      return map[estado] || 'estado-default';
    },
    actualizarReloj() {
      const now = new Date();
      this.horaActual = now.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      this.fechaActual = now.toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    },
    detenerRotacion() {
      clearInterval(this.intervaloRotacion);
      clearInterval(this.intervaloProgreso);
      this.intervaloRotacion = null;
      this.intervaloProgreso = null;
    },
    iniciarRotacion() {
      this.detenerRotacion();
      if (this.rotacionPausada) return;
      this.reiniciarProgreso();
      this.intervaloRotacion = setInterval(this.siguienteSlide, this.rotacionMs);
      this.intervaloProgreso = setInterval(() => {
        this.progresoSlide = Math.min(100, this.progresoSlide + (100 / (this.rotacionMs / this.tickProgresoMs)));
      }, this.tickProgresoMs);
    },
    alternarPausaRotacion() {
      this.rotacionPausada = !this.rotacionPausada;
      if (this.rotacionPausada) {
        this.detenerRotacion();
      } else {
        this.iniciarRotacion();
      }
    },
    reiniciarProgreso() {
      this.progresoSlide = 0;
    },
    siguienteSlide() {
      this.slideActual = (this.slideActual + 1) % this.totalSlides;
      this.reiniciarProgreso();
      if (this.slideActual === 0) this.obtenerTickets();
    },
    irASlide(idx) {
      this.slideActual = idx;
      this.reiniciarProgreso();
      this.detenerRotacion();
      if (!this.rotacionPausada) {
        this.iniciarRotacion();
      }
      if (idx === 0) this.obtenerTickets();
    },
    conectarWebSocket() {
      this.socket = connectSocket('/ws/tickets/');
      this.socket.onopen = () => console.log('Conectado al WebSocket');
      this.socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        const respuesta = data.tickets;
        this.dataTicket = respuesta.results;
        this.totalItems = respuesta.count;
        this.verificarNuevosTickets();
      };
      this.socket.onerror = (error) => console.error('WebSocket error:', error);
      this.socket.onclose = () => {
        console.warn('WebSocket desconectado. Reintentando...');
        setTimeout(this.conectarWebSocket, 5000);
      };
    },
    async obtenerTickets() {
      try {
        const respuesta = await axios.get(`${RUTA_API}/mesa_ayuda_tickets/?page=1`);
        this.dataTicket = respuesta.data.results;
        this.lastTicketSnapshot = this.dataTicket.map((ticket) => ({
          id: ticket.id,
          usuario_id: ticket.usuario_id,
        }));
        this.totalItems = respuesta.data.count;
      } catch (error) {
        console.log('Error:', error);
      }
    },
    async fetchDashboard() {
      try {
        const { data } = await axios.get(`${RUTA_API}/api/mesa-ayuda/dashboard/`, { params: { top_n: 8 } });
        this.dashboard = data || {};
      } catch (e) {
        console.error('Error fetchDashboard', e);
      }
    },
    verificarNuevosTickets() {
      try {
        const nuevosTickets = [];
        const reasignados = [];
        this.dataTicket.forEach((nuevo) => {
          const previo = this.lastTicketSnapshot.find((t) => t.id === nuevo.id);
          if (!previo) nuevosTickets.push(nuevo);
          else if (previo.usuario_id !== nuevo.usuario_id) reasignados.push(nuevo);
        });
        this.lastTicketSnapshot = this.dataTicket.map((ticket) => ({
          id: ticket.id,
          usuario_id: ticket.usuario_id,
        }));
        nuevosTickets.forEach((ticket) => {
          Notification({
            title: 'Nuevo ticket',
            message: `Se registró el ticket: "${ticket.titulo}"`,
            type: 'success',
            duration: 5000,
            showClose: false,
          });
          this.reproducirVozTicket(ticket);
        });
        reasignados.forEach((ticket) => {
          Notification({
            title: 'Ticket asignado',
            message: `Un nuevo ticket ha sido asignado a ${ticket.usuario}.`,
            type: 'success',
            duration: 3000,
            showClose: false,
          });
          this.reproducirVozUsuario(ticket);
        });
      } catch (error) {
        console.log('Error en verificación de tickets:', error);
      }
    },
    reproducirVozUsuario(item) {
      const texto = `Se le ha asignado una nueva tarea a ${item.usuario}`;
      const utterance = new SpeechSynthesisUtterance(texto);
      utterance.lang = 'es-ES';
      speechSynthesis.speak(utterance);
    },
    reproducirVozTicket(item) {
      const texto = `Se ha creado un nuevo ticket para ${item.area}`;
      const utterance = new SpeechSynthesisUtterance(texto);
      utterance.lang = 'es-ES';
      speechSynthesis.speak(utterance);
    },
  },
};
</script>

<style scoped>
.panel-tv {
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  padding: 0.85rem 1.25rem 0.75rem;
  color: #f8fafc;
  background:
    url(https://cdn.forms.office.net/images/aio/wave-pattern-v1.svg),
    linear-gradient(160deg, #0b3d7a 0%, #0f57ac 45%, #000000 100%);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  box-sizing: border-box;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.panel-brand {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.panel-logo {
  font-size: 2.5rem;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 0.5rem 0.75rem;
  backdrop-filter: blur(6px);
}

.panel-title {
  margin: 0;
  font-size: clamp(1.4rem, 2.5vw, 2rem);
  font-weight: 800;
  letter-spacing: 0.02em;
}

.panel-subtitle {
  margin: 0.2rem 0 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.95rem;
}

.panel-header-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.live-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  background: rgba(239, 68, 68, 0.18);
  border: 1px solid rgba(252, 165, 165, 0.45);
  font-weight: 700;
  font-size: 0.85rem;
}

.live-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #ef4444;
  box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  animation: pulse-live 1.6s infinite;
}

@keyframes pulse-live {
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.65); }
  70% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
}

.panel-clock {
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.panel-date {
  text-transform: capitalize;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.95rem;
}

.kpi-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.55rem;
  flex-shrink: 0;
}

.kpi-tile {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  padding: 0.5rem 0.75rem;
  backdrop-filter: blur(8px);
}

.kpi-tile-value {
  font-size: clamp(1.2rem, 2vw, 1.6rem);
  font-weight: 800;
  line-height: 1.1;
}

.kpi-tile-label {
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.78);
}

.kpi-tile--blue .kpi-tile-value { color: #93c5fd; }
.kpi-tile--amber .kpi-tile-value { color: #fcd34d; }
.kpi-tile--green .kpi-tile-value { color: #6ee7b7; }
.kpi-tile--violet .kpi-tile-value { color: #c4b5fd; }

.panel-main {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.panel-slide-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-slide-wrap > .panel-fade-enter-active,
.panel-slide-wrap > .panel-fade-leave-active,
.panel-slide-wrap > section {
  flex: 1;
  min-height: 0;
  width: 100%;
}

.panel-screen {
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  overflow: hidden;
  min-height: 0;
}

.panel-screen--charts {
  gap: 0.35rem;
}

.screen-head--compact {
  flex-shrink: 0;
}

.screen-head--compact h2 {
  font-size: clamp(1rem, 1.6vw, 1.2rem);
}

.screen-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.screen-head h2 {
  margin: 0;
  font-size: clamp(1.1rem, 2vw, 1.45rem);
  font-weight: 700;
}

.screen-badge {
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.88);
}

.table-shell {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(15, 23, 42, 0.35);
  backdrop-filter: blur(10px);
}

.tv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: clamp(0.82rem, 1.2vw, 1rem);
}

.tv-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: left;
  padding: 0.55rem 0.75rem;
  background: rgba(15, 23, 42, 0.92);
  color: #cbd5e1;
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  white-space: nowrap;
}

.tv-table tbody td {
  padding: 0.45rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  vertical-align: middle;
}

.tv-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.03);
}

.tv-row:hover {
  background: rgba(96, 165, 250, 0.12);
}

.col-ticket {
  font-weight: 700;
  color: #93c5fd;
  white-space: nowrap;
}

.col-titulo {
  font-weight: 600;
  max-width: 220px;
}

.col-fecha {
  white-space: nowrap;
  color: #cbd5e1;
}

.empty-row {
  text-align: center;
  padding: 2rem !important;
  color: #94a3b8;
}

.estado-chip {
  display: inline-block;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.estado-nuevo { background: rgba(52, 211, 153, 0.2); color: #6ee7b7; }
.estado-asignado { background: rgba(251, 191, 36, 0.2); color: #fcd34d; }
.estado-proceso { background: rgba(96, 165, 250, 0.2); color: #93c5fd; }
.estado-espera { background: rgba(45, 212, 191, 0.2); color: #5eead4; }
.estado-resuelto { background: rgba(74, 222, 128, 0.2); color: #86efac; }
.estado-cancelado { background: rgba(248, 113, 113, 0.2); color: #fca5a5; }
.estado-cerrado { background: rgba(148, 163, 184, 0.25); color: #e2e8f0; }
.estado-default { background: rgba(255, 255, 255, 0.12); color: #f1f5f9; }

.charts-grid {
  display: grid;
  gap: 0.45rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.charts-grid--tv {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(2, minmax(0, 1fr));
}

.charts-grid--tv-2col {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: minmax(0, 1fr);
  flex: 1;
}

.chart-panel--large h3 {
  font-size: 0.9rem;
  margin-bottom: 0.35rem;
}

.chart-panel--large {
  padding: 0.55rem 0.7rem 0.5rem;
}

.chart-panel {
  background: rgba(15, 23, 42, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  padding: 0.4rem 0.55rem 0.45rem;
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.chart-panel h3 {
  margin: 0 0 0.2rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #e2e8f0;
  flex-shrink: 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chart-box {
  position: relative;
  width: 100%;
  flex-shrink: 0;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.45rem;
  flex-shrink: 0;
}

.stats-row--compact {
  margin-top: 0.15rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.65rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  min-width: 0;
}

.stat-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.stat-value {
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-label {
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.75);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.panel-footer {
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  gap: 0.35rem 1rem;
  align-items: center;
  flex-shrink: 0;
}

.slide-tabs {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.slide-tab {
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
  border-radius: 999px;
  padding: 0.35rem 0.9rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;
}

.slide-tab.active {
  background: rgba(255, 255, 255, 0.22);
  border-color: rgba(255, 255, 255, 0.45);
}

.slide-tab--pause.active {
  background: rgba(251, 191, 36, 0.22);
  border-color: rgba(251, 191, 36, 0.55);
  color: #fde68a;
}

.slide-progress {
  grid-column: 1 / -1;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.15);
  overflow: hidden;
}

.slide-progress--paused {
  background: rgba(251, 191, 36, 0.2);
}

.slide-progress--paused .slide-progress-bar {
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
}

.slide-paused-label {
  color: #fde68a;
  font-weight: 600;
}

.slide-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #60a5fa, #34d399);
  border-radius: 999px;
  transition: width 0.05s linear;
}

.slide-counter {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.78);
  white-space: nowrap;
}

.panel-fade-enter-active,
.panel-fade-leave-active {
  transition: opacity 0.45s ease, transform 0.45s ease;
}

.panel-fade-enter,
.panel-fade-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (max-width: 1100px) {
  .kpi-strip,
  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .panel-tv {
    padding: 0.85rem;
  }

  .kpi-strip,
  .stats-row {
    grid-template-columns: 1fr;
  }

  .panel-header-meta {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
